import * as GeoTIFF from 'geotiff/dist-browser/geotiff'
import * as proj4 from 'proj4'
import type { Extent } from 'ol/extent'
import ImageLayer from 'ol/layer/Image'
import ImageStatic from 'ol/source/ImageStatic'

const MAX_PREVIEW_DIM = 4096

export type LocalRgbGeotiffOverlayData = {
  extent3857: Extent
  imageDataUrl: string
}

export type LocalGeotiffLayerState = LocalRgbGeotiffOverlayData & {
  id: string
  name: string
  visible: boolean
  opacity: number
}

type Proj4Module = {
  (from: string, to: string, coord: [number, number]): [number, number]
  defs: (epsg: string, def?: string) => unknown
}

function getProj4(): Proj4Module {
  return (proj4 as any).default as Proj4Module
}

/**
 * Pre-register CRS definitions that aren't built into proj4 by default but that we want to
 * support for local GeoTIFF drops. proj4 only ships EPSG:4326 and EPSG:3857; everything else
 * (every UTM zone, British National Grid, etc.) must be added via `defs` before use.
 */
function registerKnownProjections(): void {
  const proj = getProj4()
  if (typeof proj?.defs !== 'function') {
    return
  }

  // British National Grid – frequently used in this project's UK datasets.
  if (!proj.defs('EPSG:27700')) {
    proj.defs(
      'EPSG:27700',
      '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 ' +
      '+ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs +type=crs'
    )
  }

  // Irish Grid / ITM – also common in UK/IE data.
  if (!proj.defs('EPSG:29903')) {
    proj.defs(
      'EPSG:29903',
      '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000 ' +
      '+datum=ire65 +units=m +no_defs +type=crs'
    )
  }
  if (!proj.defs('EPSG:2157')) {
    proj.defs(
      'EPSG:2157',
      '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=0.99982 +x_0=600000 +y_0=750000 ' +
      '+ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
    )
  }

  // All 60 WGS84 UTM zones, north and south.
  for (let zone = 1; zone <= 60; zone++) {
    const north = `EPSG:${32600 + zone}`
    if (!proj.defs(north)) {
      proj.defs(north, `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs +type=crs`)
    }
    const south = `EPSG:${32700 + zone}`
    if (!proj.defs(south)) {
      proj.defs(south, `+proj=utm +zone=${zone} +south +datum=WGS84 +units=m +no_defs +type=crs`)
    }
  }

  // ETRS89 UTM zones 28N–38N (cover Europe; very common in EU national datasets).
  for (let zone = 28; zone <= 38; zone++) {
    const code = `EPSG:${25800 + zone}`
    if (!proj.defs(code)) {
      proj.defs(
        code,
        `+proj=utm +zone=${zone} +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs`
      )
    }
  }
  if (!proj.defs('EPSG:4258')) {
    // ETRS89 geographic.
    proj.defs(
      'EPSG:4258',
      '+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs +type=crs'
    )
  }

  // NAD83 UTM zones 1N–23N (cover North America).
  for (let zone = 1; zone <= 23; zone++) {
    const code = `EPSG:${26900 + zone}`
    if (!proj.defs(code)) {
      proj.defs(
        code,
        `+proj=utm +zone=${zone} +ellps=GRS80 +datum=NAD83 +units=m +no_defs +type=crs`
      )
    }
  }
}

registerKnownProjections()

function isEpsgKnown(epsg: string): boolean {
  const proj = getProj4()
  if (typeof proj?.defs !== 'function') {
    return false
  }
  try {
    return Boolean(proj.defs(epsg))
  } catch {
    return false
  }
}

const GEOGRAPHIC_EPSG = new Set(['EPSG:4326', 'EPSG:4258', 'EPSG:4269', 'EPSG:4267'])
const WEB_MERCATOR_LIMIT = 20037508.34 * 1.05

/** GeoTIFF GTModelTypeGeoKey: 1 = projected, 2 = geographic. */
const GT_MODEL_PROJECTED = 1
const GT_MODEL_GEOGRAPHIC = 2

const EPSG_IN_TEXT =
  /(?:AUTHORITY\s*\[\s*"EPSG"\s*,\s*"(\d+)"|EPSG[:_\s]*(\d{4,6}))/gi

function bboxLooksGeographic(bbox: number[]): boolean {
  const [minX, minY, maxX, maxY] = bbox
  return (
    minX >= -180 &&
    maxX <= 180 &&
    minY >= -90 &&
    maxY <= 90
  )
}

function bboxLooksWebMercator(bbox: number[]): boolean {
  const [minX, minY, maxX, maxY] = bbox
  const maxAbs = Math.max(Math.abs(minX), Math.abs(minY), Math.abs(maxX), Math.abs(maxY))
  return maxAbs > 180 && maxAbs <= WEB_MERCATOR_LIMIT
}

function guessEpsgFromBbox(bbox: number[]): string | null {
  if (bboxLooksGeographic(bbox)) {
    return 'EPSG:4326'
  }
  if (bboxLooksWebMercator(bbox)) {
    return 'EPSG:3857'
  }
  return null
}

function extractAllEpsgFromText(text: string): string[] {
  const found: string[] = []
  const seen = new Set<string>()
  EPSG_IN_TEXT.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = EPSG_IN_TEXT.exec(text)) !== null) {
    const code = `EPSG:${match[1] ?? match[2]}`
    if (!seen.has(code)) {
      seen.add(code)
      found.push(code)
    }
  }
  return found
}

function pickEpsgFromText(
  text: string,
  modelType: number | undefined,
  bbox: number[]
): string | null {
  const codes = extractAllEpsgFromText(text)
  if (codes.length === 0) {
    return null
  }
  if (modelType === GT_MODEL_PROJECTED || (!bboxLooksGeographic(bbox) && modelType !== GT_MODEL_GEOGRAPHIC)) {
    const projected = codes.filter(code => !GEOGRAPHIC_EPSG.has(code))
    if (projected.length > 0) {
      return projected[projected.length - 1]
    }
  }
  if (modelType === GT_MODEL_GEOGRAPHIC) {
    const geographic = codes.find(code => GEOGRAPHIC_EPSG.has(code))
    if (geographic) {
      return geographic
    }
  }
  return codes[codes.length - 1]
}

function inferEpsgFromNamePatterns(text: string): string | null {
  const utm = /UTM[\s_-]*zone[\s_-]*([0-9]{1,2})\s*([NnSs])/i.exec(text)
  if (utm) {
    const zone = Number(utm[1])
    if (Number.isFinite(zone) && zone >= 1 && zone <= 60) {
      const northern = utm[2].toUpperCase() === 'N'
      return `EPSG:${(northern ? 32600 : 32700) + zone}`
    }
  }
  if (/British\s*National\s*Grid|OSGB[\s_-]*1936/i.exec(text)) {
    return 'EPSG:27700'
  }
  return null
}

function collectCrsTextSources(
  geoKeys: Record<string, unknown> | undefined,
  fileDirectory: Record<string, unknown> | undefined
): string[] {
  const sources: string[] = []
  const push = (value: unknown) => {
    if (typeof value === 'string' && value.length > 0) {
      sources.push(value)
    }
  }

  push(geoKeys?.PCSCitationGeoKey)
  push(fileDirectory?.GeoAsciiParams)
  if (typeof fileDirectory?.GeoAsciiParams === 'string') {
    for (const part of fileDirectory.GeoAsciiParams.split('\0')) {
      push(part)
    }
  }
  push(geoKeys?.GTCitationGeoKey)
  push(geoKeys?.GeogCitationGeoKey)
  return sources
}

function reprojectionLooksValid(bbox: number[], fromEpsg: string): boolean {
  if (!isEpsgKnown(fromEpsg)) {
    return false
  }
  if (GEOGRAPHIC_EPSG.has(fromEpsg) && !bboxLooksGeographic(bbox)) {
    return false
  }
  try {
    const [minX, minY, maxX, maxY] = extentTo3857(bbox, fromEpsg)
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) {
      return false
    }
    return (
      Math.abs(minX) <= WEB_MERCATOR_LIMIT &&
      Math.abs(maxX) <= WEB_MERCATOR_LIMIT &&
      Math.abs(minY) <= WEB_MERCATOR_LIMIT &&
      Math.abs(maxY) <= WEB_MERCATOR_LIMIT
    )
  } catch {
    return false
  }
}

function addCandidate(candidates: string[], seen: Set<string>, epsg: string | null): void {
  if (!epsg || seen.has(epsg)) {
    return
  }
  seen.add(epsg)
  candidates.push(epsg)
}

/**
 * Recover the source CRS for a GeoTIFF and pick the first candidate whose bbox reprojects
 * cleanly to EPSG:3857. Many writers set ProjectedCSTypeGeoKey to 32767 ("user-defined") and
 * embed the real code in WKT AUTHORITY blocks inside GeoAsciiParams.
 */
function resolveSourceEpsg(
  geoKeys: Record<string, unknown> | undefined,
  fileDirectory: Record<string, unknown> | undefined,
  bbox: number[]
): string | null {
  const candidates: string[] = []
  const seen = new Set<string>()
  const modelType =
    typeof geoKeys?.GTModelTypeGeoKey === 'number' ? geoKeys.GTModelTypeGeoKey : undefined

  const pc = geoKeys?.ProjectedCSTypeGeoKey
  if (typeof pc === 'number' && pc > 0 && pc !== 32767) {
    addCandidate(candidates, seen, `EPSG:${pc}`)
  }

  for (const text of collectCrsTextSources(geoKeys, fileDirectory)) {
    addCandidate(candidates, seen, pickEpsgFromText(text, modelType, bbox))
    addCandidate(candidates, seen, inferEpsgFromNamePatterns(text))
  }

  const gc = geoKeys?.GeographicTypeGeoKey
  if (typeof gc === 'number' && gc > 0 && gc !== 32767) {
    if (modelType === GT_MODEL_GEOGRAPHIC || (modelType !== GT_MODEL_PROJECTED && bboxLooksGeographic(bbox))) {
      addCandidate(candidates, seen, `EPSG:${gc}`)
    }
  }

  addCandidate(candidates, seen, guessEpsgFromBbox(bbox))

  for (const epsg of candidates) {
    if (reprojectionLooksValid(bbox, epsg)) {
      return epsg
    }
  }

  return null
}

/**
 * Sample the perimeter of the source bbox before reprojecting so that the resulting EPSG:3857
 * extent stays tight when the source CRS is non-conformal with Web Mercator (UTM, BNG, etc.).
 */
function extentTo3857(bbox: number[], fromEpsg: string): Extent {
  const [minX, minY, maxX, maxY] = bbox
  const samples = 9
  const points: Array<[number, number]> = []
  for (let i = 0; i <= samples; i++) {
    const t = i / samples
    points.push([minX + (maxX - minX) * t, minY])
    points.push([minX + (maxX - minX) * t, maxY])
    points.push([minX, minY + (maxY - minY) * t])
    points.push([maxX, minY + (maxY - minY) * t])
  }
  const proj = getProj4()
  const projected = points.map(p => proj(fromEpsg, 'EPSG:3857', p))
  const xs = projected.map(p => p[0]).filter(Number.isFinite)
  const ys = projected.map(p => p[1]).filter(Number.isFinite)
  if (xs.length === 0 || ys.length === 0) {
    throw new Error('Reprojection produced no finite coordinates.')
  }
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)]
}

/** Decode a local GeoTIFF to a map-sized preview (downsampled) and geographic extent in EPSG:3857. */
export async function parseLocalRgbGeotiffPreview(arrayBuffer: ArrayBuffer): Promise<LocalRgbGeotiffOverlayData> {
  const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer)
  const image = await tiff.getImage()
  let bbox: number[]
  try {
    bbox = image.getBoundingBox()
  } catch {
    throw new Error('This GeoTIFF has no usable georeferencing (bounding box could not be read).')
  }

  const geoKeys = image.getGeoKeys() as Record<string, unknown> | undefined
  const fileDirectory = image.getFileDirectory() as Record<string, unknown> | undefined
  const fromEpsg = resolveSourceEpsg(geoKeys, fileDirectory, bbox)

  if (!fromEpsg) {
    // eslint-disable-next-line no-console
    console.warn('[local geotiff] Could not determine CRS from GeoKeys', { geoKeys, fileDirectory, bbox })
    throw new Error(
      'Could not determine the GeoTIFF\'s CRS from its GeoKeys. ' +
      'Re-export the file with an EPSG code set on the projection (e.g. via gdal_translate -a_srs EPSG:32651).'
    )
  }

  if (!isEpsgKnown(fromEpsg)) {
    // eslint-disable-next-line no-console
    console.warn(`[local geotiff] CRS ${fromEpsg} is not registered with proj4`, { geoKeys, fileDirectory, bbox })
    throw new Error(
      `GeoTIFF uses ${fromEpsg}, which isn't registered in proj4. ` +
      'Reproject to EPSG:3857 or EPSG:4326 (e.g. via gdalwarp) before dropping it on the map.'
    )
  }

  let extent3857: Extent
  try {
    extent3857 = extentTo3857(bbox, fromEpsg)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[local geotiff] Failed to reproject extent from ${fromEpsg} to EPSG:3857`, { err, bbox })
    throw new Error(
      `proj4 could not transform the GeoTIFF extent from ${fromEpsg} to EPSG:3857.`
    )
  }

  // eslint-disable-next-line no-console
  console.info('[local geotiff] CRS detected and reprojected', {
    fromEpsg,
    sourceBbox: bbox,
    extent3857,
  })

  const iw = image.getWidth()
  const ih = image.getHeight()
  const scale = Math.min(1, MAX_PREVIEW_DIM / Math.max(iw, ih))
  const outW = Math.max(1, Math.round(iw * scale))
  const outH = Math.max(1, Math.round(ih * scale))

  let rgb: Uint8Array | Uint8ClampedArray
  let channelCount = 3
  try {
    rgb = (await image.readRGB({ width: outW, height: outH, interleave: true, enableAlpha: true })) as Uint8Array
    if (rgb.length >= outW * outH * 4) {
      channelCount = 4
    }
  } catch (e) {
    const msg =
      e instanceof Error
        ? e.message
        : 'Could not decode raster as RGB. Use an RGB (or grayscale/palette) GeoTIFF.'
    throw new Error(msg)
  }

  const pixels = outW * outH
  if (rgb.length < pixels * 3) {
    throw new Error('Unexpected RGB raster size from GeoTIFF decoder.')
  }
  const noDataRaw = image.getGDALNoData()
  const noDataValue =
    typeof noDataRaw === 'number'
      ? noDataRaw
      : typeof noDataRaw === 'string'
        ? Number(noDataRaw)
        : Number.NaN
  const hasNoDataValue = Number.isFinite(noDataValue)

  const rgba = new Uint8ClampedArray(pixels * 4)
  for (let i = 0, j = 0; j < pixels * 4; i += channelCount, j += 4) {
    const r = rgb[i]
    const g = rgb[i + 1]
    const b = rgb[i + 2]
    const alphaFromSource = channelCount >= 4 ? rgb[i + 3] : 255
    const isNoData =
      hasNoDataValue &&
      r === noDataValue &&
      g === noDataValue &&
      b === noDataValue
    const isBlackFill = r === 0 && g === 0 && b === 0
    rgba[j] = r
    rgba[j + 1] = g
    rgba[j + 2] = b
    rgba[j + 3] = (isNoData || isBlackFill) ? 0 : alphaFromSource
  }

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Could not get a 2d canvas context for GeoTIFF preview.')
  }
  ctx.putImageData(new ImageData(rgba, outW, outH), 0, 0)

  return {
    extent3857,
    imageDataUrl: canvas.toDataURL('image/png'),
  }
}

export function olImageLayerFromLocalPreview(item: LocalGeotiffLayerState): ImageLayer<ImageStatic> {
  const layer = new ImageLayer({
    opacity: item.opacity,
    visible: item.visible,
    source: new ImageStatic({
      url: item.imageDataUrl,
      imageExtent: item.extent3857,
      projection: 'EPSG:3857',
    }),
  })
  layer.set('localDropGeotiff', true)
  layer.set('localDropId', item.id)
  layer.set('name', `localGeotiff:${item.id}`)
  return layer
}
